import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from '../../icons';
import { prefersReducedMotion } from '../useMediaQuery';
import './Shelf.css';

export interface ShelfScrollState {
  atStart: boolean;
  atEnd: boolean;
}

export interface ShelfDestination {
  to: string;
  label?: string;
}

export interface ShelfProps<T> {
  id: string;
  title: string;
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  getKey?: (item: T, index: number) => string;
  destination?: ShelfDestination;
  /** Overrides the h2 text as the accessible section name when needed. */
  accessibleLabel?: string;
  aspectRatio?: '1/1' | '4/3';
  /** Externally supplied end states (fixtures); otherwise measured live. */
  scrollState?: ShelfScrollState;
  onScrollStateChange?: (state: ShelfScrollState) => void;
  className?: string;
}

const EDGE_TOLERANCE = 1;

export function readScrollState(track: { scrollLeft: number; clientWidth: number; scrollWidth: number }): ShelfScrollState {
  const atStart = track.scrollLeft <= EDGE_TOLERANCE;
  const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - EDGE_TOLERANCE;
  return { atStart, atEnd };
}

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Horizontal shelf: h2 title, named Previous and Next buttons whose disabled
 * state comes from real scroll metrics, an optional See all link, and a CSS
 * grid track with proximity snapping. Tile widths are pure CSS.
 */
function Shelf<T>({
  id,
  title,
  items,
  renderItem,
  getKey,
  destination,
  accessibleLabel,
  aspectRatio = '1/1',
  scrollState,
  onScrollStateChange,
  className,
}: ShelfProps<T>) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [measured, setMeasured] = useState<ShelfScrollState>({ atStart: true, atEnd: true });
  const lastReported = useRef<ShelfScrollState | null>(null);

  const recompute = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const next = readScrollState(track);
    setMeasured((current) => (current.atStart === next.atStart && current.atEnd === next.atEnd ? current : next));
    const previous = lastReported.current;
    if (!previous || previous.atStart !== next.atStart || previous.atEnd !== next.atEnd) {
      lastReported.current = next;
      onScrollStateChange?.(next);
    }
  }, [onScrollStateChange]);

  useIsomorphicLayoutEffect(() => {
    recompute();
  }, [recompute, items.length, aspectRatio]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const onScroll = () => recompute();
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => recompute());
      observer.observe(track);
    }
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    let cancelled = false;
    fonts?.ready?.then(() => {
      if (!cancelled) recompute();
    }).catch(() => undefined);
    return () => {
      cancelled = true;
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      observer?.disconnect();
    };
  }, [recompute]);

  const state = scrollState ?? measured;

  const scrollByPage = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const distance = Math.max(track.clientWidth * 0.9, 1) * direction;
    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
    if (typeof track.scrollBy === 'function') {
      track.scrollBy({ left: distance, behavior });
    } else {
      track.scrollLeft += distance;
    }
    recompute();
  };

  const headingId = `${id}-title`;
  const classes = `luma-shelf${className ? ` ${className}` : ''}`;

  return (
    <section id={id} className={classes} aria-labelledby={headingId} aria-label={accessibleLabel} data-aspect={aspectRatio}>
      <div className="luma-shelf__head">
        <h2 id={headingId} className="luma-shelf__title">{title}</h2>
        <div className="luma-shelf__controls">
          <button
            type="button"
            className="luma-shelf__arrow"
            onClick={() => scrollByPage(-1)}
            disabled={state.atStart}
            aria-label={`Previous, ${title}`}
          >
            <ArrowLeftIcon size={22} />
          </button>
          <button
            type="button"
            className="luma-shelf__arrow"
            onClick={() => scrollByPage(1)}
            disabled={state.atEnd}
            aria-label={`Next, ${title}`}
          >
            <ArrowRightIcon size={22} />
          </button>
          {destination && (
            <Link className="luma-shelf__see-all" to={destination.to}>
              {destination.label ?? 'See all'}
              <span className="luma-sr-only">, {title}</span>
            </Link>
          )}
        </div>
      </div>
      <ul ref={trackRef} className="luma-shelf__track" data-testid={`${id}-track`}>
        {items.map((item, index) => (
          <li key={getKey ? getKey(item, index) : index} className="luma-shelf__item">
            {renderItem(item, index)}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default Shelf;
